import React, { useCallback, useEffect, useState } from "react";
import { formatNumber } from "../../../redux/utils";
import { fetchApi, getSector, postApi } from "../../../redux/api";
import moment from "moment";
import { useSearchParams } from "react-router-dom";
import BackButton from "../../../components/BackButton";
import { Button, Card, Spinner } from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faPrint } from "@fortawesome/free-solid-svg-icons";
import toast from "react-hot-toast";
import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
  pdf,
} from "@react-pdf/renderer";
import DM_SANS_NORMAL from "../../../assets/DM_Sans/DM_Sans/static/DMSans_24pt-SemiBold.ttf";
import DM_SANS_BOLD from "../../../assets/DM_Sans/DM_Sans/static/DMSans_24pt-Bold.ttf";
import DM_SANS_ITALIC from "../../../assets/DM_Sans/DM_Sans/static/DMSans-Italic.ttf";
import { useSelector } from "react-redux";

Font.register({
  family: "DM_SANS",
  fonts: [
    { src: DM_SANS_NORMAL },
    { src: DM_SANS_BOLD, fontStyle: "bold" },
    { src: DM_SANS_ITALIC, fontStyle: "italic" },
  ],
});

const pdfStyles = StyleSheet.create({
  page: {
    fontFamily: "DM_SANS",
    fontSize: 10,
    paddingTop: 30,
    paddingBottom: 30,
    paddingHorizontal: 50,
    color: "#000",
  },
  sectionCenter: { alignItems: "center", marginBottom: 16 },
  logo: { width: 70, height: 50, marginBottom: 8 },
  ministryTitle: { fontSize: 14, fontStyle: "bold", marginBottom: 4, textAlign: "center" },
  addressText: { fontSize: 9, textAlign: "center", marginBottom: 2 },
  refRow: { flexDirection: "row", justifyContent: "space-between", marginBottom: 14 },
  refText: { fontSize: 10, fontStyle: "bold", textDecoration: "underline" },
  bold: { fontStyle: "bold" },
  italic: { fontStyle: "italic" },
  subject: { textAlign: "center", marginBottom: 12 },
  subjectText: { fontSize: 11, fontStyle: "bold", textDecoration: "underline" },
  body: { fontSize: 10, lineHeight: 1.5, textAlign: "justify", marginBottom: 10 },
  sigBlock: { marginTop: 20, alignItems: "flex-end" },
  sigName: { fontSize: 10, fontStyle: "bold" },
  sigTitle: { fontSize: 9, fontStyle: "bold" },
  ccLabel: { fontSize: 9, fontStyle: "bold", marginTop: 14, marginBottom: 6 },
  ccItem: { fontSize: 8, fontStyle: "italic", marginBottom: 2, marginLeft: 14 },
  footer: { fontSize: 9, fontStyle: "italic", textAlign: "center", marginTop: 14, marginBottom: 10 },
  watermark: {
    position: "absolute",
    top: "40%",
    left: 0,
    right: 0,
    fontSize: 36,
    fontStyle: "bold",
    color: "#000000",
    opacity: 0.05,
    textAlign: "center",
    transform: "rotate(-45deg)",
  },
});

/**
 * PDF document built with @react-pdf/renderer — renders two pages of the release letter.
 */
const ReleasePdfDocument = ({ form, tableData, directorName, directorSignature, ignoreMDA, taday, isAgView = false, user }) => {
  const isMultiItem = tableData && tableData.length > 1;
  const copies1Title = form?.copies1?.officeTitle || "";
  const copiesTitle = form?.copies?.officeTitle || "";

  const sigName = directorName || "KABIR ABU BAZARIYE";

  const ccItems = [
    ...(!ignoreMDA && copiesTitle ? [`${copiesTitle}, Katsina,`] : []),
    ...(copies1Title ? [`${copies1Title}, Katsina,`] : []),
    "The Permanent Secretary, Government House, Katsina,",
    "The Hon. Commissioner, Ministry of Finance, Katsina,",
    "The Auditor General, Office of the Auditor-General for the State, Katsina,",
    "The Chief of Staff, Government House, Katsina,",
    "The Chairman, Fiscal Responsibility Commission, Katsina,",
    "The Director Main-Account, Ministry of Finance, Katsina,",
    ...(form.cc === "kirs" ? ["Board Of Internal Revenue, Katsina,"] : []),
  ];

  const TableHeader = () => (
    <View style={{ flexDirection: "row", borderBottomWidth: 1, borderBottomColor: "#000", backgroundColor: "#f0f0f0", padding: 4 }}>
      <Text style={{ width: "25%", fontSize: 9, fontStyle: "bold" }}>MDA Code</Text>
      <Text style={{ width: "25%", fontSize: 9, fontStyle: "bold" }}>Economic Code</Text>
      <Text style={{ width: "30%", fontSize: 9, fontStyle: "bold" }}>Description</Text>
      <Text style={{ width: "20%", fontSize: 9, fontStyle: "bold", textAlign: "right" }}>Amount (N)</Text>
    </View>
  );

  const TableRow = ({ item }) => (
    <View style={{ flexDirection: "row", borderBottomWidth: 0.5, borderBottomColor: "#ccc", padding: 4 }}>
      <Text style={{ width: "25%", fontSize: 8 }}>{item.mda_code}</Text>
      <Text style={{ width: "25%", fontSize: 8 }}>{item.mda_economic_code || item.economic_code}</Text>
      <Text style={{ width: "30%", fontSize: 8 }}>{item.description_code || item.description}</Text>
      <Text style={{ width: "20%", fontSize: 8, textAlign: "right" }}>{formatNumber(item.amount)}</Text>
    </View>
  );

  const PageContent = ({ isPage2 = false }) => {
    const voteCode = `${form.projectType === "payable-to" ? form.send_to_office : form.mda_code
      }/${form.approval_type === "Recurrent" ? form.economic_code : form.program_code
      }`;

    const body1 = `I am directed to convey approval for the release of (N${formatNumber(
      form.amount
    )}) only to ${copiesTitle ? copiesTitle + " " : ""
      }${copies1Title ? "payable to the " : ""
      }${copies1Title}${form.narration || ""}. The Vote of Charge is ${voteCode}.`;

    const body2 = `This letter is copied to the ${ignoreMDA ? "" : copiesTitle} ${copies1Title ? copies1Title + "," : ""
      } the PS, (GHKT), the HC (MOF), the Auditor-General, the Office of the Auditor-General for the State, and the Director of Main Account, Ministry of Finance.`;

    const multiBody = `I am directed to convey approval for the release of funds for recurrent services/special programs in respect of ${form.narration || 'the approved projects'} to Ministries, Departments and Agencies as follows:`;

    return (
      <>
        <Text style={pdfStyles.watermark}>Ministry of Budget and Economic Planning</Text>
        <View style={pdfStyles.sectionCenter}>
          <Image src={require("../../../assets/img/KTSG.png")} style={pdfStyles.logo} />
          <Text style={pdfStyles.ministryTitle}>MINISTRY OF BUDGET &amp; ECONOMIC PLANNING</Text>
          <Text style={pdfStyles.addressText}>Old Government House Office Complex, Katsina</Text>
          <Text style={[pdfStyles.addressText, pdfStyles.italic]}>P.MB 2132, KATSINA</Text>
        </View>

        <View style={pdfStyles.refRow}>
          <View style={{ flexDirection: "row" }}>
            <Text style={pdfStyles.bold}>Our Ref: </Text>
            <Text style={pdfStyles.refText}>{form?.approval_no}</Text>
          </View>
          <Text style={pdfStyles.refText}>{taday}</Text>
        </View>

        <View style={{ marginBottom: 14 }}>
          <Text style={pdfStyles.italic}>The Accountant General,</Text>
          <Text style={pdfStyles.italic}>Ministry of Finance,</Text>
          <Text style={pdfStyles.italic}>Katsina,</Text>
        </View>

        <View style={pdfStyles.subject}>
          <Text style={pdfStyles.subjectText}>SPECIAL RELEASE OF FUNDS</Text>
        </View>

        {isMultiItem ? (
          <>
            <Text style={[pdfStyles.body, { marginBottom: 10 }]}>{multiBody}</Text>
            <TableHeader />
            {tableData.map((item, i) => (
              <TableRow key={i} item={item} />
            ))}
            <View style={{ flexDirection: "row", padding: 4, marginTop: 4, borderTopWidth: 1 }}>
              <Text style={{ width: "80%", fontSize: 9, fontStyle: "bold", textAlign: "right" }}>TOTAL:</Text>
              <Text style={{ width: "20%", fontSize: 9, fontStyle: "bold", textAlign: "right" }}>N{formatNumber(tableData.reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0))}</Text>
            </View>
          </>
        ) : (
          <>
            <Text style={pdfStyles.body}>{body1}</Text>
          </>
        )}

        <Text style={[pdfStyles.body, { marginTop: 10 }]}>{body2}</Text>

        {!isPage2 && (
          <View style={pdfStyles.sigBlock}>
            {directorSignature && <Image src={directorSignature} style={{ width: 60, height: 40, marginBottom: 4 }} />}
            <Text style={pdfStyles.sigName}>{sigName}</Text>
            <Text style={pdfStyles.sigTitle}>DIRECTOR OF BUDGET</Text>
            <Text style={pdfStyles.sigTitle}>FOR: HON. COMMISSIONER</Text>
          </View>
        )}

        {!isPage2 && (
          <View style={{ marginTop: 10 }}>
            <Text style={pdfStyles.ccLabel}>CC:</Text>
            {ccItems.map((item, i) => (
              <Text key={i} style={pdfStyles.ccItem}>{item}</Text>
            ))}
          </View>
        )}

        <Text style={pdfStyles.footer}>Above for your information and awareness, please.</Text>

        <View style={pdfStyles.sigBlock}>
          {directorSignature && <Image src={directorSignature} style={{ width: 60, height: 40, marginBottom: 4 }} />}
          <Text style={pdfStyles.sigName}>{sigName}</Text>
          <Text style={pdfStyles.sigTitle}>DIRECTOR OF BUDGET</Text>
          <Text style={pdfStyles.sigTitle}>FOR: HON. COMMISSIONER</Text>
        </View>
      </>
    );
  };

  return (
    <Document>
      {/* Page 2: letter addressed to Accountant General — shown for AG view */}
      {isAgView && (
        <Page size="A4" style={pdfStyles.page}>
          <PageContent isPage2={true} />
        </Page>
      )}
      {/* Page 1: letter addressed to the MDA — shown for all other origins */}
      {!isAgView && (
        <Page size="A4" style={pdfStyles.page}>
          <PageContent isPage2={false} />
        </Page>
      )}
    </Document>
  );
};

export const ReleaseCollectionHTML = () => {
  const [loading, setLoading] = useState(false);
  const [loadingDirector, setLoadingDirector] = useState(false);
  const [loadingSignature, setLoadingSignature] = useState(false);
  const [loadingCc, setLoadingCc] = useState(false);
  const [searchParams] = useSearchParams();
  const approval_no = searchParams.get("approval_no");
  // `from=ag` is appended by AG Dashboard / AG Approved pages.
  // When present → show Page 1 only. Any other origin → show Page 2 only.
  const isAgView = searchParams.get("from") === "ag";
  const isCc = searchParams.get("from") === "cc";
  const isExpc = searchParams.get("from") === "expc";
  const isTracking = searchParams.get("from") === "release-tracking"
  const user = useSelector(s => s.auth.user)

  const [form, setForm] = useState({
    collection_date: "",
    approval_date: "",
    approval_type: "",
    mda_name: "",
    amount: "",
    status: "",
    economic_code: "",
    approval_no: "",
    mda_economic_code: "",
  });

  const taday = moment(form.approval_date).format("Do MMMM, YYYY");
  const [signatureInfo, setSignatureInfo] = useState({});
  const [directors, setDirectors] = useState([]);
  const [ccCopies, setCcCopies] = useState([]);
  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    if (form.approved_ids) {
      setLoadingSignature(true);
      fetchApi(`sign-by-id-new?id=${form.approved_ids}`)
        .then((d) => {
          if (d.success) {
            setSignatureInfo(d?.data[0]);
          }
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => setLoadingSignature(false));
    }
  }, [form.approved_ids]);

  const getCcCopieds = (mda_name) => {
    setLoadingCc(true);
    postApi(`post-cc?query_type=select_mda`, { mda_name })
      .then((data) => {
        if (data.results && data.results.length) {
          setCcCopies(data.results);
          setForm((p) => ({
            ...p,
            copies1: data.results[0],
          }));
        }
      })
      .catch((err) => {
        console.error("Error fetching ccCopies:", err);
      })
      .finally(() => setLoadingCc(false));
  };

  const getCcToPay = (mda_name) => {
    postApi(`post-cc?query_type=select_mda`, { mda_name })
      .then((data) => {
        if (data.results && data.results.length) {
          setCcCopies(data.results);
          setForm((p) => ({
            ...p,
            beneficiary: data.results[0],
          }));
        }
      })
      .catch((err) => {
        console.error("Error fetching ccCopies:", err);
      });
  };

  useEffect(() => {
    if (form.approved_ids) {
      setLoadingDirector(true);
      postApi(`account_tbeams?query_type=select_director&head=${form.approved_ids}`, {})
        .then((data) => {
          if (data.success && data?.results) {
            setDirectors(data.results);
          }
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => setLoadingDirector(false));
    }
  }, [form.approved_ids]);

  const getApprovalCollection = useCallback(() => {
    if (!approval_no) {
      toast.error("Approval number is required");
      return;
    }

    setLoading(true);
    postApi(`post_approval_collection`, {
      form: {
        query_type: isAgView || isCc || isExpc || isTracking ? "select_by_approval_nos" : "select_mda_detail_by_approval_no",
        approval_no: approval_no,
        mda_name: user?.mda_name,
        mda_code: user?.mda_code
      },
    })
      .then((data) => {
        if (data.result && data.result.length) {
          setTableData(data.result);
          const result = data.result[0];
          setForm(result);
          getCcCopieds(result.mda_name);
          getCcToPay(result.mda_head)
        }
      })
      .catch((err) => {
        console.error("Error fetching approval collection:", err);
        toast.error("Failed to load approval data");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [approval_no]);

  useEffect(() => {
    getApprovalCollection();
  }, [getApprovalCollection]);

  const ignoreMDA =
    form.mda_name === "Ministry of Finance" ||
    form.mda_name === "Government House";
  const directorName = directors[0]?.name;
  const directorSignature = directors[0]?.imageURL;

  const totalAmount = tableData.reduce(
    (acc, curr) => acc + (parseFloat(curr.amount) || 0),
    0
  );

  // ─── Inline style definitions (matching NewRelisePdfWebView) ───────────────

  const pageStyle = {
    width: "210mm",
    minHeight: "297mm",
    padding: "20px 50px 20px 50px",
    fontFamily: "Arial, sans-serif",
    fontSize: "10px",
    backgroundColor: "white",
    margin: "0 auto 20px",
    boxShadow: "0 0 10px rgba(0,0,0,0.1)",
    position: "relative",
    boxSizing: "border-box",
  };

  const pageBreakStyle = {
    ...pageStyle,
    pageBreakBefore: "always",
  };

  const watermarkStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%) rotate(-45deg)",
    fontSize: "48px",
    fontWeight: "bold",
    color: "rgba(0, 0, 0, 0.05)",
    whiteSpace: "nowrap",
    pointerEvents: "none",
    userSelect: "none",
    zIndex: 1,
  };

  const contentWrapperStyle = {
    position: "relative",
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
    height: "100%",
  };

  // ─── PDF / Print handlers ─────────────────────────────────────────────────

  /**
   * Auto-download as a real PDF using @react-pdf/renderer.
   * Generates a Blob, creates an object URL, and clicks a hidden anchor — no dialog.
   */
  const handleDownloadPDF = async () => {
    try {
      const blob = await pdf(
        <ReleasePdfDocument
          form={form}
          tableData={tableData}
          directorName={directorName}
          directorSignature={directorSignature}
          ignoreMDA={ignoreMDA}
          taday={taday}
          isAgView={isAgView}
          user={user}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `release-${form.approval_no || "document"}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF generation failed:", err);
      toast.error("Failed to generate PDF");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // ─── Loading state ────────────────────────────────────────────────────────

  const isFullyLoaded = !loading && !loadingDirector && !loadingSignature && !loadingCc;

  if (!isFullyLoaded) {
    return (
      <div
        className="d-flex flex-column justify-content-center align-items-center"
        style={{ height: "400px" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3 text-muted">Loading release document...</p>
      </div>
    );
  }

  // ─── Shared page header sub-component ────────────────────────────────────

  const PageHeader = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        paddingTop: "20px",
        marginBottom: "20px",
      }}
    >
      <img
        src={require("../../../assets/img/KTSG.png")}
        style={{ width: "100px", height: "60px", marginBottom: "8px" }}
        alt="logo"
      />
      <div style={{ fontSize: "17px", fontWeight: "bold", marginTop: "10px" }}>
        MINISTRY OF BUDGET &amp; ECONOMIC PLANNING
      </div>
      <div style={{ fontSize: "10px", fontWeight: "bold" }}>
        Old Government House Office Complex, Katsina
      </div>
      <div style={{ fontSize: "12px", fontStyle: "italic" }}>
        P.MB 2132, KATSINA
      </div>
    </div>
  );

  // ─── Shared ref / date row ─────────────────────────────────────────────────

  const RefDateRow = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        paddingBottom: "10px",
        marginTop: "10px",
      }}
    >
      <div style={{ width: "70%", fontWeight: "bold" }}>
        <span>Our Ref: </span>
        <span style={{ textDecoration: "underline" }}>{form?.approval_no}</span>
      </div>
      <div
        style={{
          width: "30%",
          textDecoration: "underline",
          fontWeight: "bold",
          textAlign: "right",
        }}
      >
        {taday}
      </div>
    </div>
  );

  // ─── Shared subject title ──────────────────────────────────────────────────

  const SubjectTitle = () => (
    <div style={{ width: "100%", textAlign: "center", marginTop: "10px" }}>
      <div
        style={{
          textDecoration: "underline",
          fontWeight: "bold",
          fontSize: "11px",
          textTransform: "uppercase",
        }}
      >
        SPECIAL RELEASE OF FUNDS
      </div>
    </div>
  );

  // ─── Shared signature block ────────────────────────────────────────────────

  const SignatureBlock = ({ align = "right" }) => (
    <div
      style={{
        width: "35%",
        textAlign: align,
        marginLeft: align === "right" ? "auto" : undefined,
      }}
    >
      {directorSignature && (
        <img
          src={directorSignature}
          style={{ width: "80px", height: "60px", marginBottom: "5px" }}
          alt="signature"
        />
      )}
      <div
        style={{
          fontWeight: "bold",
          fontSize: "10px",
          marginBottom: "2px",
          textAlign: "center",
        }}
      >
        {directorName || "KABIR ABU BAZARIYE"}
      </div>
      <div style={{ fontWeight: "bold", fontSize: "9px", textAlign: "center" }}>
        DIRECTOR OF BUDGET
      </div>
      <div style={{ fontWeight: "bold", fontSize: "9px", textAlign: "center" }}>
        FOR: HON. COMMISSIONER
      </div>
    </div>
  );

  // ─── CC section ────────────────────────────────────────────────────────────

  const CcSection = () => (
    <div style={{ width: "60%", fontSize: "10px" }}>
      <div style={{ fontWeight: "bold", marginBottom: "8px" }}>CC:</div>
      <div
        style={{
          marginLeft: "15px",
          fontStyle: "italic",
          fontSize: "8px",
        }}
      >
        {!ignoreMDA && form?.copies?.officeTitle && (
          <div style={{ marginBottom: "1px" }}>
            {form?.copies?.officeTitle}, Katsina,
          </div>
        )}
        {form?.copies1?.officeTitle && (
          <div style={{ marginBottom: "1px" }}>
            {form?.copies1?.officeTitle}, Katsina,
          </div>
        )}
        {form?.beneficiary?.officeTitle && (
          <div style={{ marginBottom: "1px" }}>
            {form?.beneficiary?.officeTitle}, Katsina,
          </div>
        )}
        <div style={{ marginBottom: "1px" }}>
          The Permanent Secretary, Government House, Katsina,
        </div>
        <div style={{ marginBottom: "1px" }}>
          The Hon. Commissioner, Ministry of Finance, Katsina,
        </div>
        <div style={{ marginBottom: "1px" }}>
          The Auditor General, Office of the Auditor-General for the State,
          Katsina,
        </div>
        <div style={{ marginBottom: "1px" }}>
          The Chief of Staff, Government House, Katsina,
        </div>
        <div style={{ marginBottom: "1px" }}>
          The Chairman, Fiscal Responsibility Commission, Katsina,
        </div>
        <div style={{ marginBottom: "1px" }}>
          The Director Main-Account, Ministry of Finance, Katsina,
        </div>
        {form.cc === "kirs" && (
          <div style={{ marginBottom: "1px" }}>
            Board Of Internal Revenue, Katsina,
          </div>
        )}
      </div>
    </div>
  );

  // ─── Main render ──────────────────────────────────────────────────────────

  return (
    <>
      {/* Toolbar — hidden on print */}
      <div className="d-print-none">
        <BackButton />
        <Card border="light" className="bg-white shadow-sm mb-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Release Document - {form.approval_no}</h5>
              <div className="d-flex gap-2">
                <Button variant="primary" onClick={handleDownloadPDF}>
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                  Download PDF
                </Button>
                <Button variant="secondary" onClick={handlePrint}>
                  <FontAwesomeIcon icon={faPrint} className="me-2" />
                  Print
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* Document wrapper */}
      <div
        id="release-document"
        style={{
          backgroundColor: "#f5f5f5",
          padding: "20px",
          minHeight: "100vh",
        }}
      >
        {/* ═══ Full letter — shown for all non-AG origins ═══ */}
        {!isAgView && <div style={pageStyle}>
          <div style={watermarkStyle}>
            Ministry of Budget and Economic Planning
          </div>

          <div style={contentWrapperStyle}>
            <PageHeader />
            <RefDateRow />

            {/* Addressee */}
            <div style={{ width: "50%", marginBottom: "20px", marginTop: "10px" }}>
              <div style={{ fontStyle: "italic", fontSize: "11px", margin: "3px 0" }}>
                The Accountant General,
              </div>
              <div style={{ fontStyle: "italic", fontSize: "11px", margin: "3px 0" }}>
                Ministry of Finance,
              </div>
              <div style={{ fontStyle: "italic", fontSize: "11px", margin: "3px 0" }}>
                Katsina,
              </div>
            </div>

            <SubjectTitle />

            {/* Body */}
            <div
              style={{
                width: "100%",
                textAlign: "justify",
                marginTop: "7px",
                marginBottom: "20px",
              }}
            >
              {tableData.length > 1 ? (
                <>
                  <p style={{ fontSize: "11px", lineHeight: "1.5", margin: "15px 0" }}>
                    I am directed to convey approval for the release of funds for recurrent services/special programs in respect of {form.narration || 'the approved projects'} to Ministries, Departments and Agencies as follows:
                  </p>
                  <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px", fontSize: "10px" }}>
                    <thead style={{ backgroundColor: "#f0f0f0" }}>
                      <tr>
                        <th style={{ border: "1px solid #ccc", padding: "5px" }}>MDA Code</th>
                        <th style={{ border: "1px solid #ccc", padding: "5px" }}>Economic Code</th>
                        <th style={{ border: "1px solid #ccc", padding: "5px" }}>Description</th>
                        <th style={{ border: "1px solid #ccc", padding: "5px", textAlign: "right" }}>Amount (N)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((item, i) => (
                        <tr key={i}>
                          <td style={{ border: "1px solid #ccc", padding: "5px" }}>{item.mda_code}</td>
                          <td style={{ border: "1px solid #ccc", padding: "5px" }}>{item.mda_economic_code || item.economic_code}</td>
                          <td style={{ border: "1px solid #ccc", padding: "5px" }}>{item.description_code || item.description}</td>
                          <td style={{ border: "1px solid #ccc", padding: "5px", textAlign: "right" }}>{formatNumber(item.amount)}</td>
                        </tr>
                      ))}
                      <tr style={{ fontWeight: "bold" }}>
                        <td colSpan="3" style={{ border: "1px solid #ccc", padding: "5px", textAlign: "right" }}>TOTAL:</td>
                        <td style={{ border: "1px solid #ccc", padding: "5px", textAlign: "right" }}>N{formatNumber(totalAmount)}</td>
                      </tr>
                    </tbody>
                  </table>
                </>
              ) : (
                // <p style={{ fontSize: "11px", lineHeight: "1.5", margin: "15px 0" }}>
                //   I am directed to convey approval for the release of{" "}
                //   <strong>(N{formatNumber(form.amount)}) only</strong> to{" "}
                //   {form?.copies1?.officeTitle && form?.copies1?.officeTitle !== null
                //     ? form?.copies1?.officeTitle + " "
                //     : ""}
                //   {form?.copies?.officeTitle && form?.copies?.officeTitle !== null
                //     ? " payable to the "
                //     : ""}{" "}
                //   {form?.copies?.officeTitle}
                //   {form.narration}.{" "}
                //   <strong>
                //     The Vote of Charge is{" "}
                //     {form.projectType === "payable-to"
                //       ? form.send_to_office
                //       : form.mda_code}
                //     /
                //     {form.approval_type === "Recurrent"
                //       ? form.economic_code
                //       : form.program_code}
                //     .
                //   </strong>
                // </p>
                <div>
                  <p style={{ fontSize: "11px", lineHeight: "1.5", margin: "15px 0" }}>
                    I am directed to convey approval for the release of{" "}
                    <span style={{ fontWeight: "bold" }}>
                      (N{formatNumber(form.amount)}) only
                    </span>{" "}
                    to {form?.copies1?.officeTitle}
                    {form?.beneficiary?.officeTitle && form?.beneficiary?.officeTitle !== null
                      ? " payable to "
                      : ""}{" "}
                    {form?.beneficiary?.officeTitle && form?.beneficiary?.officeTitle !== null
                      ? form?.beneficiary?.officeTitle + " "
                      : ""}
                    {form.narration}.
                  </p>
                  <p style={{ marginTop: "10px", fontWeight: "bold", fontSize: "11px", lineHeight: "1.5" }}>
                    The Vote of Charge is{" "}
                    {form.project_type === "payable-to"
                      ? form.send_to_office
                      : form.mda_code}
                    /
                    {form.approval_type === "Recurrent"
                      ? form.economic_code
                      : form.program_code}
                    .
                  </p>
                </div>
              )}

              {/* {JSON.stringify(form)}xx */}

              {/* <p style={{ fontSize: "11px", lineHeight: "1.5", margin: "15px 0" }}>
                This letter is copied to the{" "}
                {ignoreMDA ? null : form?.copies?.officeTitle}{" "}
                {form?.copies1 && form?.copies1?.officeTitle}, the PS, (GHKT),
                the HC (MOF), the Auditor-General, the Office of the
                Auditor-General for the State, and the Director of Main
                Account, Ministry of Finance.
              </p> */}
              <p style={{ fontSize: "11px", lineHeight: "1.5", margin: "15px 0" }}>
                This letter is copied to{" "}
                {ignoreMDA ? null : form?.copies?.officeTitle}{" "}
                {form?.copies1?.officeTitle
                  ? form?.copies1?.officeTitle + ", "
                  : ""}
                  {form?.beneficiary?.officeTitle
                  ? form?.beneficiary?.officeTitle + ", "
                  : ""}
                {form?.cc ? form?.cc + ", " : ""}
                the PS, (GHKT), the HC (MOF), the COS (GHKT), the Chairman (FRC),
                the Auditor-General, the Office of the Auditor-General for the
                State, the Chairman, and the Director Main Account, Ministry of
                Finance.
              </p>
            </div>

            {/* Signature (top) */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                margin: "20px 0",
              }}
            >
              <SignatureBlock align="center" />
            </div>

            {/* CC + Signature row */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "20px",
              }}
            >
              <CcSection />
              <SignatureBlock />
            </div>

            {/* Footer note */}
            <div
              style={{
                textAlign: "center",
                fontSize: "10px",
                fontStyle: "italic",
                marginTop: "15px",
                marginBottom: "10px",
              }}
            >
              Above for your information and awareness, please.
            </div>
          </div>
        </div>}

        {/* ═══ Shorter letter — shown when coming from AG Dashboard / AG Approved ═══ */}
        {isAgView && <div style={pageBreakStyle}>
          <div style={watermarkStyle}>
            Ministry of Budget and Economic Planning
          </div>

          <div style={contentWrapperStyle}>
            <PageHeader />
            <RefDateRow />

            {/* Addressee */}
            <div style={{ width: "50%", marginBottom: "20px", marginTop: "10px" }}>
              <div style={{ fontStyle: "italic", fontSize: "11px", margin: "3px 0" }}>
                The Accountant General,
              </div>
              <div style={{ fontStyle: "italic", fontSize: "11px", margin: "3px 0" }}>
                Ministry of Finance,
              </div>
              <div style={{ fontStyle: "italic", fontSize: "11px", margin: "3px 0" }}>
                Katsina,
              </div>
            </div>

            <SubjectTitle />

            {/* Body */}
            <div
              style={{
                width: "100%",
                textAlign: "justify",
                marginTop: "7px",
                marginBottom: "20px",
              }}
            >
              {tableData.length > 1 ? (
                <>
                  <p style={{ fontSize: "11px", lineHeight: "1.5", margin: "15px 0" }}>
                    I am directed to convey approval for the release of funds for recurrent services/special programs in respect of {form.narration || 'the approved projects'} to Ministries, Departments and Agencies as follows:
                  </p>
                  <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px", fontSize: "10px" }}>
                    <thead style={{ backgroundColor: "#f0f0f0" }}>
                      <tr>
                        <th style={{ border: "1px solid #ccc", padding: "5px" }}>MDA Code</th>
                        <th style={{ border: "1px solid #ccc", padding: "5px" }}>Economic Code</th>
                        <th style={{ border: "1px solid #ccc", padding: "5px" }}>Description</th>
                        <th style={{ border: "1px solid #ccc", padding: "5px", textAlign: "right" }}>Amount (N)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((item, i) => (
                        <tr key={i}>
                          <td style={{ border: "1px solid #ccc", padding: "5px" }}>{item.mda_code}</td>
                          <td style={{ border: "1px solid #ccc", padding: "5px" }}>{item.mda_economic_code || item.economic_code}</td>
                          <td style={{ border: "1px solid #ccc", padding: "5px" }}>{item.description_code || item.description}</td>
                          <td style={{ border: "1px solid #ccc", padding: "5px", textAlign: "right" }}>{formatNumber(item.amount)}</td>
                        </tr>
                      ))}
                      <tr style={{ fontWeight: "bold" }}>
                        <td colSpan="3" style={{ border: "1px solid #ccc", padding: "5px", textAlign: "right" }}>TOTAL:</td>
                        <td style={{ border: "1px solid #ccc", padding: "5px", textAlign: "right" }}>N{formatNumber(totalAmount)}</td>
                      </tr>
                    </tbody>
                  </table>
                </>
              ) : (
               <div>
                  <p style={{ fontSize: "11px", lineHeight: "1.5", margin: "15px 0" }}>
                    I am directed to convey approval for the release of{" "}
                    <span style={{ fontWeight: "bold" }}>
                      (N{formatNumber(form.amount)}) only
                    </span>{" "}
                    to {form?.copies1?.officeTitle}
                    {form?.copies1?.officeTitle && form?.copies1?.officeTitle !== null
                      ? " payable to "
                      : ""}{" "}
                    {form?.beneficiary?.officeTitle && form?.beneficiary?.officeTitle !== null
                      ? form?.beneficiary?.officeTitle + " "
                      : ""}
                    {form.narration}.
                  </p>
                  <p style={{ marginTop: "10px", fontWeight: "bold", fontSize: "11px", lineHeight: "1.5" }}>
                    The Vote of Charge is{" "}
                    {form.project_type === "payable-to"
                      ? form.send_to_office
                      : form.mda_code}
                    /
                    {form.approval_type === "Recurrent"
                      ? form.economic_code
                      : form.program_code}
                    .
                  </p>
                </div>
              )}

              <p style={{ fontSize: "11px", lineHeight: "1.5", margin: "15px 0" }}>
                This letter is copied to the{" "}
                {ignoreMDA ? null : form?.copies?.officeTitle},{" "}
                {form?.copies1 && form?.copies1?.officeTitle}, {form?.beneficiary?.officeTitle
                  ? form?.beneficiary?.officeTitle + ", "
                  : ""}, the PS, (GHKT),
                the HC (MOF), the Auditor-General, the Office of the
                Auditor-General for the State, and the Director of Main
                Account, Ministry of Finance.
              </p>
            </div>

            {/* Final signature pushed to bottom-right */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "auto",
                paddingTop: "40px",
              }}
            >
              <SignatureBlock />
            </div>
          </div>
        </div>}
      </div>
    </>
  );
};